import type { ReactNode } from 'react';

export const extractTextContent = (node: ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }

  if (typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(extractTextContent).join(' ');
  }

  if (node && typeof node === 'object' && 'props' in node) {
    const reactElement = node as { props?: { children?: ReactNode } };
    return extractTextContent(reactElement.props?.children || '');
  }

  return '';
};

export const generateAriaLabel = (
  title?: string,
  children?: ReactNode,
  fallback: string = 'Link',
): string => {
  if (title && typeof title === 'string' && title.trim().length > 0 && !isInvalidAriaText(title)) {
    return title.trim();
  }

  if (children) {
    const textContent = extractTextContent(children);
    if (textContent && textContent.trim().length > 0 && !isInvalidAriaText(textContent)) {
      return textContent.trim();
    }
  }

  return fallback;
};

const isInvalidAriaText = (text: string): boolean => {
  const trimmedText = text.trim();

  const invalidPatterns = [/^\*+$/, /^[*\s]+$/, /^[\u00A0\s]*$/, /^[^\w\s]*$/];

  return invalidPatterns.some((pattern) => pattern.test(trimmedText)) || trimmedText.length === 0;
};

export const applyAriaLabelFallbacks = (container: HTMLElement): void => {
  const elementsNeedingLabels = container.querySelectorAll('a, button, input, select, textarea');

  elementsNeedingLabels.forEach((element) => {
    const htmlElement = element as HTMLElement;
    const currentLabel = htmlElement.getAttribute('aria-label')?.trim();

    if (!currentLabel || isInvalidAriaText(currentLabel)) {
      const textContent = htmlElement.textContent?.trim();
      const title = htmlElement.getAttribute('title')?.trim();
      const tagName = htmlElement.tagName.toLowerCase();

      let fallbackLabel = '';

      if (textContent && !isInvalidAriaText(textContent)) {
        fallbackLabel = textContent;
      } else if (title && !isInvalidAriaText(title)) {
        fallbackLabel = title;
      } else {
        switch (tagName) {
          case 'a':
            if (htmlElement.classList.contains('audi-j-footnote-reference')) {
              const footnoteNumber = extractFootnoteNumber(htmlElement);
              fallbackLabel = footnoteNumber ? `Footnote ${footnoteNumber}` : 'Footnote reference';
            } else {
              fallbackLabel = 'Link';
            }
            break;
          case 'button':
            fallbackLabel = 'Button';
            break;
          case 'input':
            fallbackLabel = 'Input field';
            break;
          case 'select':
            fallbackLabel = 'Select dropdown';
            break;
          case 'textarea':
            fallbackLabel = 'Text area';
            break;
          default:
            fallbackLabel = 'Interactive element';
        }
      }

      if (fallbackLabel) {
        htmlElement.setAttribute('aria-label', fallbackLabel);
      }
    }
  });
};

const extractFootnoteNumber = (element: HTMLElement): string => {
  const href = element.getAttribute('href');
  if (href) {
    const match = href.match(/footnote(\d+)|ft_\w*(\d+)/i);
    if (match) {
      return match[1] || match[2];
    }
  }

  const dataAttrs = element.getAttributeNames().filter((name) => name.startsWith('data-'));
  for (const attr of dataAttrs) {
    const value = element.getAttribute(attr);
    if (value) {
      const match = value.match(/(\d+)/);
      if (match) {
        return match[1];
      }
    }
  }

  const parentText = element.parentElement?.textContent || '';
  const numberMatch = parentText.match(/(\d+)/);

  return numberMatch ? numberMatch[1] : '';
};
